import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ALL_PERIODS, getPeriodName } from '../../../periods';

/**
 * Picker tipográfico de rango de fechas, editorial style.
 * Trigger: "vista al · <descripción>". Click abre un popover con presets + custom.
 *
 * Las keys de los presets son las mismas que usa el Data Explorer (`ALL_PERIODS`)
 * para que el período guardado en `view.config.period` se muestre con el label
 * exacto que ve el agency en el Explorer.
 */

// Subset de períodos relevantes para el portal del cliente (sin THIS_PERIOD que
// requiere contexto de la BU).
const PORTAL_PERIODS = ALL_PERIODS.filter(
    (p) => !['THIS_PERIOD', 'THIS_PERIOD_TILL_YESTERDAY', '7_DAYS_BEFORE', '14_DAYS_BEFORE'].includes(p)
);

function presetLabel(value, t) {
    if (!value) return t('portal_editorial.range_default');
    if (value.predefinedPeriod) {
        const name = getPeriodName(value.predefinedPeriod, t);
        return name || value.predefinedPeriod;
    }
    if (value.startDate && value.endDate) {
        const from = moment(value.startDate).format('D MMM');
        const to = moment(value.endDate).format('D MMM YYYY');
        return `${from} → ${to}`;
    }
    return t('portal_editorial.range_default');
}

export function PortalDateRangePicker({ value, compareValue, onChange, periods = null }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const apply = (newValue) => {
        if (onChange) onChange(newValue);
        setOpen(false);
    };

    const applyCustom = () => {
        if (!customStart || !customEnd) return;
        apply({ startDate: customStart, endDate: customEnd });
    };

    return (
        <div className="portal-date-picker" ref={ref}>
            <button
                type="button"
                className="portal-date-picker-trigger"
                onClick={() => setOpen((o) => !o)}
            >
                <span className="portal-date-picker-eyebrow">
                    {t('portal_editorial.range_eyebrow')}
                </span>
                <span className="portal-date-picker-value">
                    {presetLabel(value, t)}
                </span>
                {compareValue && (
                    <span className="portal-date-picker-compare">
                        {t('portal_editorial.range_vs', { compare: presetLabel(compareValue, t) })}
                    </span>
                )}
            </button>

            {open && (
                <div className="portal-date-picker-popover">
                    <div className="portal-date-picker-presets">
                        {(periods || PORTAL_PERIODS).map((key) => (
                            <button
                                key={key}
                                type="button"
                                className="portal-date-picker-preset"
                                onClick={() => apply({ predefinedPeriod: key })}
                            >
                                {getPeriodName(key, t)}
                            </button>
                        ))}
                    </div>
                    <div className="portal-date-picker-custom">
                        <span className="portal-date-picker-section-title">
                            {t('portal_editorial.range_custom')}
                        </span>
                        <div className="portal-date-picker-custom-row">
                            <input
                                type="date"
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                aria-label={t('portal_editorial.range_from')}
                            />
                            <span className="portal-date-picker-arrow">→</span>
                            <input
                                type="date"
                                value={customEnd}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                aria-label={t('portal_editorial.range_to')}
                            />
                            <button
                                type="button"
                                className="portal-date-picker-apply"
                                onClick={applyCustom}
                                disabled={!customStart || !customEnd}
                            >
                                {t('portal_editorial.range_apply')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
